import { Admin } from './admin';
import { Moderator } from './moderator';

export enum ContentType {
  gpsdd = 'gpsdd',
  json = 'json',
  image = 'image',
  video = 'video',
  audio = 'audio',
  text = 'text'
}

export interface AADGroup {
  groupId: string;
  groupName: string;
  lastUpdated: string;
}

export interface TenantAdmin {
  privsRevoked: boolean;
  displayName: string;
  grantedTimestamp: string;
  lastUpdated: string;
  revokedTimestamp: string;
  userPrincipalName: string;
}

export interface TenantFull extends Tenant {
  groups: AADGroup[];
  lastUpdated: string;
  members: TenantAdmin[];
}

export interface Tenant {
  displayName: string;
  tenantId: string;
  stackingConfigurationByTitleId: Record<string, TitleStackingConfiguration>;
}

export interface TitleStackingConfiguration {
  enabled: boolean;
  lookbackWindowInDays: number;
  maxPoints: number;
}

export interface Title {
  displayName: string;
  titleId: string;
}

export interface QueueConfiguration {
  allowedAdmins: Admin[];
  allowedModerators: Moderator[];
  caseAssignedLifetimeInSeconds: number;
  caseOpenLifetimeInSeconds: number;
  caseOpenLifetimeWarningFactor: number;
  contentType: ContentType;
  uxPreferences: QueueUxPreferences;
  id: string;
  moderatorFeedbackCommentOptional: boolean;
  revision: number;
  showCaseContext: boolean;
  notAssignable: boolean;
  subjectType: string;
  tenant: Tenant;
  title: Title;
  displayName: string;
  caseOpenLifetimeMaxInSeconds: number;
  defaultCaseComparerAlgorithm: string;
  defaultMaxAssignedCaseCount: number;
  categorizedTraits: Record<string, string[]>;
  categorizedTraitDefinitions?: Record<string, Record<string, ITraitDefinition>>;
  // TODO Deliverable: https://microsoft.visualstudio.com/Xbox/_workitems/edit/37800859
  sourceTypeWeightMappings: {
    Moderator: number;
    CommunitySift?: number;
    AutoModXuid?: number; // todo: are these used?
    Default?: number; // todo: are these used?
    Xuid?: number; // todo: are these used?
  };
  minimumModeratorFeedbackAgreementCount?: number;
  useUsersFromAadGroup?: boolean;
}

// make flat array
export const flatCategoryTraits = (catdef: Record<string, Record<string, ITraitDefinition>>): IFlatTraitDef[] => {
  const flatArray: IFlatTraitDef[] = [];
  Object.keys(catdef).forEach(category => {
    Object.keys(catdef[category]).forEach(trait => {
      flatArray.push({ ...catdef[category][trait], category, trait });
    });
  });
  return flatArray;
};

export interface ITraitDefinition {
  definition: string;
  examples: string[];
  exemptions: string[];
}

export interface IFlatTraitDef extends ITraitDefinition {
  category: string;
  trait: string;
}

export interface TitleQueueMap {
  [titleId: string]: {
    displayName: string;
    value: QueueConfiguration[];
  };
}

export interface QueueStats {
  casesTotal: number;
  casesAssignedTotal: number;
  casesTotalExceedingCaseOpenLifetime: number;
  casesTotalNearingCaseOpenLifetime: number;
  moderatorsAssigned: Array<UserStat>;
}

export interface UserStat {
  id: string;
  userId: string;
  preferredUserName: string;
}

export interface QueueUxPreferences {
  maxCardHeight?: number;
  disableFiltersOnSelect: boolean;
  showCardTitleGradient: boolean;
  showReportContext: boolean;
  showCardZoomOption: boolean;
  decide: {
    showTimer: boolean;
    labelsPrompt: Record<string, unknown>[];
    labelsTop?: string[];
    categoriesTop?: string[];
    maxAllowedLabels: number;
  };
  feedback: {
    isDefaultCollapsed: boolean;
  };
  subjectImage: SubjectImagePreferences;
  subjectJson: SubjectJsonPreferences;
}

export interface SubjectImagePreferences {
  blurStrengthPixels: number;
  maxHeight?: number;
  grayScalePercent?: number;
  isDefaultBlur: boolean;
  isDefaultFlipped: boolean;
  isDefaultGrayscale: boolean;
  isDefaultMirrored: boolean;
  isTextBlurred?: boolean;
}

export interface SubjectJsonPreferences {
  customCss: boolean;
  // enables/disables carousel animation for json content/imageCarousel cases
  animationLoopIsActive?: boolean;
  // used to set the carousel animation for json content/imageCarousel cases, in seconds.
  // example: 2 sets animation to 2 second wait between images transition
  animationLoopSpeed?: number;
}

export const DefaultUxPreferences: QueueUxPreferences = {
  maxCardHeight: undefined,
  showReportContext: true,
  showCardZoomOption: true,
  disableFiltersOnSelect: false,
  showCardTitleGradient: false,
  decide: {
    showTimer: true,
    labelsPrompt: [{}],
    labelsTop: [],
    categoriesTop: [],
    maxAllowedLabels: 1
  },
  feedback: {
    isDefaultCollapsed: false
  },
  subjectImage: {
    blurStrengthPixels: 10,
    isDefaultBlur: true,
    isDefaultFlipped: false,
    isDefaultGrayscale: false,
    isDefaultMirrored: false,
    isTextBlurred: false
  },
  subjectJson: {
    customCss: false
  }
};

export const isAllFilterOff = (filter: SubjectImagePreferences): boolean =>
  !filter.isDefaultBlur && !filter.isDefaultFlipped && !filter.isDefaultGrayscale && !filter.isDefaultMirrored;

export function getFilterOnState(filter: SubjectImagePreferences): SubjectImagePreferences {
  return {
    ...filter,
    isDefaultBlur: true,
    isDefaultFlipped: true,
    isDefaultGrayscale: true,
    isDefaultMirrored: true,
    isTextBlurred: true
  };
}
